import {ErrorHandler, inject, Injectable, Injector} from "@angular/core";
import {MessageService} from "primeng/api";
import {HttpErrorResponse} from "@angular/common/http";
import {PlatformService} from "./services/platform.service";
import {AnalyticsService} from "./services/analytics.service";

@Injectable()
export class GlobalErrorHandle implements ErrorHandler {
  private readonly platformService = inject(PlatformService);
  private readonly injector = inject(Injector)
  private readonly messageService = inject(MessageService);

  private shouldNotShowError(error: unknown): boolean {
    if (error instanceof Error) {
      if (error.name === 'AbortError' ||
        error.name.includes('History API') ||
        error.message.includes('History API') ||
        error.message.includes('Importing a module script failed') ||
        error.message.includes('Database deleted by request of the user') ||
        error.message.includes('During hydration Angular') ||
        error.message.includes('s.replaceAll is not a function')
      ) {
        // Ignore abort errors
        return true;
      }
    }
    return false;
  }

  private isUserCancelled(error: unknown): boolean {
    if (error === 'USER_CANCELLED') {
      return true;
    }
    if (error !== null && typeof error === 'object') {
      const e = error as {message?: unknown; errorMessage?: unknown; error?: unknown};
      return e.message === 'USER_CANCELLED' || e.errorMessage === 'USER_CANCELLED' || e.error === 'USER_CANCELLED';
    }
    return false;
  }

  handleError(error: unknown): void {
    if (this.isUserCancelled(error)) {
      console.warn('User cancelled native flow', error);
      return;
    }

    let message: string;
    let errorDetail: string;

    let visibleText = 'visible';
    let path: string | undefined;
    const online = typeof navigator !== 'undefined' ? navigator.onLine : false;
    let type: string;
    let status: string;
    let name: string;
    const preventShow = this.shouldNotShowError(error);

    if (preventShow) {
      visibleText = 'hidden';
    }

    if (error instanceof HttpErrorResponse) {
      path = error.url ?? undefined;
      type = 'http';
      status = error.status.toString();
      name = error.name;
      errorDetail = error.error && typeof error.error === 'object' && 'message' in error.error
        ? error.error.message
        : error.message;

      if (error.status === 0) {
        message = 'Unable to connect to the server. Please check your internet connection.';
      } else if (error.status === 404) {
        message = 'The requested page or resource could not be found.';
      } else {
        message = 'Something went wrong. Please try again later.';
      }

      console.error(`A ${visibleText} http error occurred (${error.status}):`, errorDetail, error, error.error);
    } else if (error instanceof Error) {
        message = error.message;
        errorDetail = error.message;
        type = 'regular';
        name = error.name;
        status = 'none';
        console.error(`A ${visibleText} error occurred :`, errorDetail, error, error.stack);
    } else {
      message = 'Something went wrong. Please try again later.';
      errorDetail = 'Error unknown';
      console.error(`A ${visibleText} string error occurred :`, errorDetail, error);
      type = 'unknown';
      name = 'unknown';
      status = 'none';
    }

    if (this.platformService.isServer || preventShow) {
      return;
    }

    this.injector.get(AnalyticsService).sendEvent('visible-error', {
      error: message,
      errorDetail,
      online: online ? 'yes' : 'no',
      path: path ?? 'no-path',
      type,
      status,
      name
    });

    this.messageService.add({
      severity: 'error',
      summary: 'An error occurred',
      detail: message
    });
  }
}
